<template>
  <div class="signup-form">
    <form
      ref="form"
      class="signup-form__form form-group"
      novalidate
      @submit="handleSubmit"
    >
      <div
        class="signup-form__choices radio"
        :class="{ [$class('error')]: choiceError }"
      >
        <template
          v-for="(list, index) of klaviyoLists"
          :key="`klaviyo-choice-${index}`"
        >
          <input
            :id="sectionId + '-signupFormOption' + index"
            v-model="choice"
            class="radio__input"
            :disabled="state === 'submitting' || state === 'success'"
            name="g"
            required
            type="radio"
            :value="list.inputValue"
          >

          <label
            class="radio__label text-body-2xs"
            :for="sectionId + '-signupFormOption' + index"
            v-text="list.radioLabel"
          />
        </template>
      </div>

      <p
        v-if="choiceError"
        class="signup-form__error text-body-2xs"
        v-text="choiceError"
      />

      <input
        v-if="source"
        name="$source"
        type="hidden"
        :value="source"
      >

      <div
        class="signup-form__input-wrapper"
        :class="{
          [$class('error')]: emailError,
          'signup-success': state === 'success'
        }"
      >
        <label
          class="visually-hidden"
          :for="sectionId + 'signupFormEmail'"
          v-text="$string('general.signup_form.email_label')"
        />

        <input
          :id="sectionId + 'signupFormEmail'"
          v-model="emailAddress"
          class="signup-form__input input text-body-s"
          :disabled="state === 'submitting' || state === 'success'"
          name="email"
          :placeholder="$string('general.signup_form.email_placeholder')"
          required
          type="email"
        >

        <btn
          class="signup-form__submit"
          :class="{ 'signup-form__submit--success': state === 'success' }"
          :disabled="state === 'submitting' || state === 'success'"
          :label="
            state === 'success'
              ? $string('accessibility.success')
              : $string('general.signup_form.submit_label')
          "
          label-class="text-button-xs"
          :show-icon="false"
          :show-state-icon="state === 'success'"
          type="submit"
        >
          <template #stateIcon>
            <icon-tick />
          </template>
        </btn>
      </div>

      <p
        v-if="emailError"
        class="signup-form__error text-body-2xs"
        v-text="emailError"
      />

      <div
        class="signup-form__consent checkbox"
        :class="{ [$class('error')]: consentError }"
      >
        <input
          :id="sectionId + 'signupFormConsent'"
          v-model="consent"
          class="checkbox__input"
          :disabled="state === 'submitting' || state === 'success'"
          name="$consent"
          type="checkbox"
          value="email"
        >

        <label
          class="checkbox__label text-body-2xs"
          :for="sectionId + 'signupFormConsent'"
          v-html="marketingConsentText"
        />
      </div>

      <p
        v-if="consentError"
        class="signup-form__error text-body-2xs"
        v-text="consentError"
      />
    </form>
  </div>
</template>

<script>

/**
 * Vue: Signup form (signup-form)
 * -----------------------------------------------------------------------------
 * Signup form for email marketing.
 *
 * @param {String} [sectionId] - Keeps all IDs unique if there are multiple
 * forms on the page.
 * @param {Array} [klaviyoListData] - List of Klaviyo IDs and corresponding
 * radio label.
 * @param {String} [source] - Identifier for the signup form used in Klaviio.
 */
import subscribe from '~/apis/klaviyo'
import { formatKlaviyoData } from '~/helpers/format'

import Btn from '~global/btn/btn'

import IconTick from '~icons/misc/tick.svg'

export default {
  name: 'SignupForm',

  components: {
    Btn,
    IconTick,
  },

  props: {
    klaviyoListData: {
      type: Array,
      default: () => [],
    },
    sectionId: {
      type: String,
      default: '',
    },
    source: {
      type: String,
      default: '',
    },
  },

  data() {
    return {
      choice: false,
      choiceError: null,
      consent: false,
      consentError: null,
      emailAddress: null,
      emailError: null,
      state: 'ready',
    }
  },

  computed: {

    /**
     * Test whether the email address contains '@' and '.' symbols.
     * - A simple regex test is used, to avoid blocking valid email addresses.
     * - The `type="email"` attribute on the input element allows for email
     *   validation in most modern browsers.
     * @returns {Boolean}
     */
    emailIsValid() {
      return (/\S+@\S+\.\S+/).test(this.emailAddress)
    },

    /**
     * Compute Klaviyo lists from prop.
     * @returns {Array}
     */
    klaviyoLists() {
      return this.klaviyoListData.map((list) => {
        return { ...list }
      })
    },

    /**
     * Compute marketing consent text.
     * @returns {String}
     */
    marketingConsentText() {
      const localeUrl = cnvs.routes.normalisedRoot === '/' ? '' : cnvs.routes.root

      return this.$string(
        'general.signup_form.marketing_consent_html',
        { replace: { locale_url: localeUrl } },
      )
    },
  },

  watch: {

    /**
     * Watch klaviyo lists update to set default choice.
     */
    klaviyoLists() {
      this.choice = this.klaviyoLists[0]?.inputValue
    },
  },

  methods: {

    /**
     * Handle form submission events.
     * @param {Event} event - Submit event from the form element.
     */
    handleSubmit(event) {
      event.preventDefault()

      this.choiceError = null
      this.consentError = null
      this.emailError = null
      this.state = 'submitting'

      if (!this.choice) {
        this.choiceError = this.$string('general.signup_form.error_choice')
      }

      if (!this.consent) {
        this.consentError = this.$string('general.signup_form.error_consent')
      }

      if (!this.emailIsValid) {
        this.emailError = this.$string('general.signup_form.error_invalid')
      }

      if (
        this.choiceError ||
        this.consentError ||
        this.emailError
      ) {
        this.state = 'error'
        return
      }

      const data = formatKlaviyoData(new FormData(this.$refs.form))

      subscribe(data)
        .then(() => {
          this.state = 'success'
        })
        .catch(() => {
          this.state = 'error'
          this.emailError =
            this.$string('general.signup_form.error_generic')
        })
    },
  },
}
</script>

<style lang="scss">
@import './signup-form';
</style>
